import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { meQueryKey } from '../api/me';
import type { WorkingDay } from '../api/types';
import type { Conflict, Page, Schedule, Series, WorkRule } from '../staff/types';
import type {
  AdminChild, AdminRecord, AdminReferral, AdminSettings, AdminSpecialist, AssignConflict, BatchStats,
  AuditEntry, CenterDay, Direction, Family, NotificationSettings, PauseResult, Service, Settings, Subscription, SubscriptionInfo,
} from './types';

/** Запросы админки центра. */

export const adminKeys = {
  settings: ['admin', 'settings'] as const,
  subscription: ['admin', 'subscription'] as const,
  admins: ['admin', 'admins'] as const,
  directions: ['admin', 'directions'] as const,
  specialists: ['admin', 'specialists'] as const,
  schedule: (id: number) => ['admin', 'schedule', id] as const,
  services: ['admin', 'services'] as const,
  clients: ['admin', 'clients'] as const,
  referrals: ['admin', 'referrals'] as const,
  child: (id: number) => ['admin', 'child', id] as const,
  center: (date: string) => ['admin', 'center', date] as const,
  batch: (id: number) => ['admin', 'batch', id] as const,
  audit: (role: string) => ['admin', 'audit', role] as const,
};

function useRefreshAdmin() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['admin'] });
    void qc.invalidateQueries({ queryKey: meQueryKey });
  };
}

export const useAdminSettings = () =>
  useQuery({ queryKey: adminKeys.settings, queryFn: () => api.get<AdminSettings>('/api/admin/settings') });

export const useAdminList = () =>
  useQuery({ queryKey: adminKeys.admins, queryFn: () => api.get<AdminRecord[]>('/api/admin/admins') });

/**
 * Подписка организации. Расчёт перехода (зачёт остатка, доплата, бонусные дни)
 * делает сервер: считать деньги на двух сторонах — верный способ разойтись.
 */
export const useSubscription = () =>
  useQuery({ queryKey: adminKeys.subscription, queryFn: () => api.get<SubscriptionInfo>('/api/admin/subscription') });

export function useSubscriptionMutations() {
  const refresh = useRefreshAdmin();
  const cancel = useMutation({
    mutationFn: () => api.post<Subscription>('/api/admin/subscription/cancel'),
    onSuccess: refresh,
  });
  const resume = useMutation({
    mutationFn: () => api.post<Subscription>('/api/admin/subscription/resume'),
    onSuccess: refresh,
  });
  const pay = useMutation({
    mutationFn: (plan: string) => api.post<{ confirmationUrl: string }>('/api/admin/subscription/pay', { plan }),
  });
  const change = useMutation({
    mutationFn: (plan: string) =>
      api.post<{ applied: boolean; subscription?: Subscription; confirmationUrl?: string; dueKop?: number }>(
        '/api/admin/subscription/change', { plan },
      ),
    onSuccess: refresh,
  });
  return { cancel, resume, pay, change };
}

/**
 * Права администратора: правка своей карточки, приглашение, снятие прав и выход.
 * Выход — отдельная мутация, а не флаг у remove: снятие прав с себя не тот
 * случай, где стоит экономить на явности.
 */
export function useAdminMutations() {
  const refresh = useRefreshAdmin();
  const updateMe = useMutation({
    mutationFn: (b: { displayName?: string; tgUsername?: string }) =>
      api.patch<AdminRecord>('/api/admin/admins/me', b),
    onSuccess: refresh,
  });
  const invite = useMutation({
    mutationFn: (b: { displayName?: string; tgUsername?: string }) =>
      api.post<{ link: string | null; code: string; expiresAt: number | null }>('/api/admin/admins/invite', b),
  });
  const remove = useMutation({
    mutationFn: (tgId: number) => api.delete<AdminRecord[]>(`/api/admin/admins/${tgId}`),
    onSuccess: refresh,
  });
  const leave = useMutation({
    mutationFn: () => api.post<{ ok: true }>('/api/admin/admins/leave'),
    onSuccess: refresh,
  });
  return { updateMe, invite, remove, leave };
}

export const useAdminDirections = () =>
  useQuery({ queryKey: adminKeys.directions, queryFn: () => api.get<Direction[]>('/api/admin/directions') });

export const useAdminSpecialists = () =>
  useQuery({ queryKey: adminKeys.specialists, queryFn: () => api.get<AdminSpecialist[]>('/api/admin/specialists') });

/** Карточка ребёнка целиком: занятия и историю в список семей не тянем — они нужны только здесь. */
export const useAdminChild = (id: number) =>
  useQuery({ queryKey: adminKeys.child(id), queryFn: () => api.get<AdminChild>(`/api/admin/children/${id}`) });

export const useAdminServices = () =>
  useQuery({ queryKey: adminKeys.services, queryFn: () => api.get<Service[]>('/api/admin/services') });

/** Семьи страницами: список центра растёт, а экран должен открываться сразу. */
export const useAdminClients = (search = '', limit = 50) =>
  useInfiniteQuery({
    queryKey: [...adminKeys.clients, search, limit],
    queryFn: ({ pageParam }) =>
      api.get<Page<Family>>('/api/admin/clients', { limit, offset: pageParam, search: search || undefined }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.offset + last.items.length < last.total ? last.offset + last.limit : undefined),
  });

/**
 * Журнал действий: страницами, свежие записи сверху. Фильтр по роли живёт в
 * ключе запроса — переключение вкладок не должно доклеивать чужие строки к
 * уже загруженным.
 */
/**
 * Срезы журнала. Считает их база: журнал растёт тысячами строк, и фильтровать
 * уже загруженную страницу значило бы врать в счётчике «осталось».
 */
export type AuditFilters = {
  /** Источник — роль того, кто совершил действие. Пусто — любой. */
  role: string;
  /** Тип события. Пусто — любой. */
  type: string;
  /** Начало периода, epoch ms. Границу дня считает экран: день у организации свой. */
  from: number | null;
};

export const useAuditLog = (f: AuditFilters, limit = 30) =>
  useInfiniteQuery({
    // Все три среза в ключе: переключение фильтра не должно доклеивать чужие
    // строки к уже загруженным.
    queryKey: adminKeys.audit(`${f.role}|${f.type}|${f.from ?? ''}`),
    queryFn: ({ pageParam }) =>
      api.get<Page<AuditEntry>>('/api/admin/audit', {
        limit,
        offset: pageParam,
        role: f.role || undefined,
        type: f.type || undefined,
        from: f.from ?? undefined,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.offset + last.items.length < last.total ? last.offset + last.limit : undefined),
  });

export function useUpdateSpecialist() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (v: {
      id: number; displayName?: string; title?: string; photoUrl?: string;
      isAdmin?: boolean; directionIds?: number[];
    }) => api.patch<AdminSpecialist>(`/api/admin/specialists/${v.id}`, v),
    onSuccess: refresh,
  });
}

export const useAdminReferrals = () =>
  useQuery({ queryKey: adminKeys.referrals, queryFn: () => api.get<AdminReferral[]>('/api/admin/referrals') });

export const useCenterDay = (date: string) =>
  useQuery({ queryKey: adminKeys.center(date), queryFn: () => api.get<CenterDay>('/api/admin/center', { date }) });

/** Чужое расписание админ видит целиком, но правит его специалист. */
export const useSpecialistSchedule = (id: number | null) =>
  useQuery({
    queryKey: adminKeys.schedule(id ?? 0),
    queryFn: () => api.get<Schedule>(`/api/admin/specialists/${id}/schedule`),
    enabled: id !== null,
  });

export const useBatchStats = (id: number | null) =>
  useQuery({
    queryKey: adminKeys.batch(id ?? 0),
    queryFn: () => api.get<BatchStats>(`/api/admin/batches/${id}`),
    enabled: id !== null,
    // Очередь разбирает воркер: пока сообщения уходят, статистика меняется.
    refetchInterval: (q) => ((q.state.data?.queued ?? 0) > 0 ? 3000 : false),
  });

/** Часы работы и уведомления едут отдельными полями: в базе это JSON, а экран о том знать не должен. */
export type SettingsPatch = Partial<Settings> & { hours?: WorkingDay[]; notifications?: NotificationSettings };

export function useSaveSettings() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (patch: SettingsPatch) => api.patch<Settings>('/api/admin/settings', patch),
    onSuccess: refresh,
  });
}

export function useLocationMutations() {
  const refresh = useRefreshAdmin();
  const create = useMutation({
    mutationFn: (b: { title: string; address: string; comment?: string; isPrimary?: boolean }) =>
      api.post<unknown>('/api/admin/locations', b),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: (v: { id: number; title?: string; address?: string; comment?: string; isPrimary?: boolean }) =>
      api.patch<unknown>(`/api/admin/locations/${v.id}`, {
        title: v.title, address: v.address, comment: v.comment,
        is_primary: v.isPrimary === undefined ? undefined : Number(v.isPrimary),
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/api/admin/locations/${id}`),
    onSuccess: refresh,
  });
  return { create, update, remove };
}

export function useDirectionMutations() {
  const refresh = useRefreshAdmin();
  const create = useMutation({
    mutationFn: (b: { title: string; slug?: string }) => api.post<Direction>('/api/admin/directions', b),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: (v: { id: number; title?: string; isActive?: boolean }) =>
      api.patch<Direction[]>(`/api/admin/directions/${v.id}`, { title: v.title, is_active: v.isActive }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (v: { id: number; moveStaffTo?: number }) =>
      api.delete<{ ok: true; services: number; staffMoved: number; archived: boolean }>(
        `/api/admin/directions/${v.id}${v.moveStaffTo ? `?moveStaffTo=${v.moveStaffTo}` : ''}`,
      ),
    onSuccess: refresh,
  });
  // Порядок уезжает целым списком: перетащили одну строку — сдвинулись все.
  const reorder = useMutation({
    mutationFn: (ids: number[]) => api.post<Direction[]>('/api/admin/directions/reorder', { ids }),
    onSuccess: refresh,
  });
  return { create, update, remove, reorder };
}

export function useCreateSpecialist() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (b: { directionIds: number[]; displayName: string; title?: string; isAdmin?: boolean; tgUsername?: string }) =>
      api.post<{ specialist: AdminSpecialist; inviteLink: string | null }>('/api/admin/specialists', b),
    onSuccess: refresh,
  });
}

/** Правка чужой сетки: специалисту уйдёт дифф — это его расписание, не админа. */
export function useSaveSpecialistSchedule() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (v: { id: number; rules: WorkRule[]; bufferMin?: number; horizonDays?: number; minLeadHours?: number }) =>
      api.patch<Schedule>(`/api/admin/specialists/${v.id}/schedule`, v),
    onSuccess: refresh,
  });
}

export function useValidateSpecialistSchedule() {
  return useMutation({
    mutationFn: (v: { id: number; rules: WorkRule[] }) =>
      api.post<{ conflicts: Conflict[] }>(`/api/admin/specialists/${v.id}/schedule/validate`, { rules: v.rules }),
  });
}

export function usePauseSpecialist() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (v: { id: number; until: number | null; reason: string; appointments: 'keep' | 'cancel' | 'notify'; template?: string }) =>
      api.post<PauseResult>(`/api/admin/specialists/${v.id}/pause`, v),
    onSuccess: refresh,
  });
}

export function useResumeSpecialist() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (id: number) => api.post<AdminSpecialist>(`/api/admin/specialists/${id}/resume`),
    onSuccess: refresh,
  });
}

/** Архивация — та же развилка, что у паузы: судьбу будущих записей выбирает админ. */
export function useArchiveSpecialist() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (v: { id: number; appointments: 'keep' | 'cancel' | 'notify'; template?: string }) =>
      api.post<PauseResult>(`/api/admin/specialists/${v.id}/archive`, { appointments: v.appointments, template: v.template }),
    onSuccess: refresh,
  });
}

export function useServiceMutations() {
  const refresh = useRefreshAdmin();
  const create = useMutation({
    mutationFn: (b: { directionId: number; title: string; durationMin: number; basePriceKop: number; isOnline?: boolean; isIntake?: boolean }) =>
      api.post<Service>('/api/admin/services', b),
    onSuccess: refresh,
  });
  // Услуга правится snake_case-полями — так их принимает сервер.
  const update = useMutation({
    mutationFn: (v: {
      id: number; title?: string; duration_min?: number; base_price_kop?: number;
      is_online?: number; is_active?: number; direction_id?: number;
    }) => api.patch<Service>(`/api/admin/services/${v.id}`, v),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/api/admin/services/${id}`),
    onSuccess: refresh,
  });
  // Порядок задаётся в границах категории: сервер ждёт её id и весь список услуг.
  const reorder = useMutation({
    mutationFn: (v: { directionId: number; ids: number[] }) =>
      api.post<Service[]>('/api/admin/services/reorder', v),
    onSuccess: refresh,
  });
  return { create, update, remove, reorder };
}

/** Базовые условия оплаты специалиста — одна ставка на все услуги его категорий. */
export function useBasePayout() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (v: { id: number; mode: 'fixed' | 'percent'; payoutKop?: number | null; payoutPercent?: number | null }) =>
      api.put<AdminSpecialist>(`/api/admin/specialists/${v.id}/payout`, v),
    onSuccess: refresh,
  });
}

export function useRateMutations() {
  const refresh = useRefreshAdmin();
  const save = useMutation({
    mutationFn: (v: {
      specialistId: number; serviceId: number; priceKop?: number | null;
      payoutMode?: 'fixed' | 'percent'; payoutKop?: number | null; payoutPercent?: number | null; isActive?: boolean;
    }) => api.put<unknown>(`/api/admin/rates/${v.specialistId}/${v.serviceId}`, v),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (v: { specialistId: number; serviceId: number }) =>
      api.delete<unknown>(`/api/admin/rates/${v.specialistId}/${v.serviceId}`),
    onSuccess: refresh,
  });
  return { save, remove };
}

export function useCreateFamily() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (b: {
      childName: string; birthYear?: number | null; note?: string; parentName: string;
      phone: string; directionId?: number | null; specialistId?: number | null; requestSeries?: boolean;
      /** Двойник найден, но администратор решил всё равно завести карточку. */
      force?: boolean;
    }) => api.post<{ client: { id: number }; child: { id: number }; inviteLink: string | null; seriesRequested: boolean }>(
      '/api/admin/clients', b,
    ),
    onSuccess: refresh,
  });
}

/**
 * Правка карточки клиента. Ручки две, потому что в базе это две строки: сам
 * клиент (имя, возраст, запрос) и контактное лицо (имя, телефон). Экран зовёт
 * только ту, где что-то поменялось — иначе смена имени клиента сбрасывала бы
 * подтверждение телефона.
 */
export function useUpdateFamily() {
  const refresh = useRefreshAdmin();
  const child = useMutation({
    mutationFn: (v: { childId: number; name?: string; birthYear?: number | null; note?: string }) =>
      api.patch<AdminChild['child']>(`/api/admin/children/${v.childId}`, {
        name: v.name, birthYear: v.birthYear, note: v.note,
      }),
    onSuccess: refresh,
  });
  const contact = useMutation({
    mutationFn: (v: { clientId: number; parentName?: string; phone?: string }) =>
      api.patch<unknown>(`/api/admin/clients/${v.clientId}/contact`, { parentName: v.parentName, phone: v.phone }),
    onSuccess: refresh,
  });
  return { child, contact };
}

/**
 * Ссылка-приглашение специалисту повторно. Мутация, а не запрос: сервер может
 * выпустить новый код, и делать это на каждом рендере карточки незачем.
 */
export function useStaffInviteLink() {
  return useMutation({
    mutationFn: (id: number) => api.post<{ link: string }>(`/api/admin/specialists/${id}/invite-link`),
  });
}

/**
 * Передать карточку другому Telegram. С обновлением списка: привязка исчезла,
 * и карточка должна показывать это сразу, а не после следующего захода.
 */
export function useTransferSpecialist() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ specialist: AdminSpecialist; link: string }>(`/api/admin/specialists/${id}/transfer`),
    onSuccess: refresh,
  });
}

export function useInviteLink() {
  return useMutation({
    mutationFn: (clientId: number) => api.post<{ link: string }>(`/api/admin/clients/${clientId}/invite-link`),
  });
}

/**
 * Отвязать Telegram от карточки родителя: по ссылке пришёл не тот человек.
 * С обновлением списка — привязки больше нет, и карточка должна показать это
 * сразу, а не после следующего захода.
 */
export function useUnlinkClient() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (clientId: number) =>
      api.post<{ client: { id: number; tg_id: number | null }; link: string }>(`/api/admin/clients/${clientId}/unlink`),
    onSuccess: refresh,
  });
}

export function useAssign() {
  const refresh = useRefreshAdmin();
  const validate = useMutation({
    mutationFn: (v: { childId: number; directionId: number }) =>
      api.post<{ conflicts: AssignConflict[] }>(`/api/admin/children/${v.childId}/assign/validate`, { directionId: v.directionId }),
  });
  const assign = useMutation({
    mutationFn: (v: { childId: number; directionId: number; specialistId: number }) =>
      api.post<unknown>(`/api/admin/children/${v.childId}/assign`, { directionId: v.directionId, specialistId: v.specialistId }),
    onSuccess: refresh,
  });
  const unassign = useMutation({
    mutationFn: (v: { childId: number; directionId: number }) =>
      api.delete<unknown>(`/api/admin/children/${v.childId}/assign/${v.directionId}`),
    onSuccess: refresh,
  });
  return { validate, assign, unassign };
}

/** Расписание занятий ведёт специалист — админ может только попросить. */
export function useRequestSeries() {
  return useMutation({
    mutationFn: (v: { childId: number; specialistId: number }) =>
      api.post<{ ok: true; notified: boolean }>(`/api/admin/children/${v.childId}/series-request`, { specialistId: v.specialistId }),
  });
}

export function useDeclineReferral() {
  const refresh = useRefreshAdmin();
  return useMutation({
    mutationFn: (id: number) => api.post<unknown>(`/api/admin/referrals/${id}/decline`),
    onSuccess: refresh,
  });
}

export const useAdminSeries = (childId: number | null) =>
  useQuery({
    queryKey: ['admin', 'series', childId],
    queryFn: () => api.get<Series[]>('/api/admin/series', { childId: childId ?? undefined }),
    enabled: childId !== null,
  });
